import {
  componentTreeToPrompt,
  TAILWIND_INSTRUCTIONS,
  type TransformOptions,
} from '@moatless/figma'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { ClipboardInput } from '@/components/clipboard-input'
import { ClipboardTestOutput, ClipboardTestOutputEmpty } from '@/components/clipboard-test-output'
import { TransformOptionsPanel } from '@/components/transform-options-panel'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useFigmaTestParser } from '@/hooks/use-figma-test-parser'

type InstructionPreset = 'none' | 'tailwind'

export function App() {
  const [clipboardInput, setClipboardInput] = useState('')
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false)
  const [instructionPreset, setInstructionPreset] = useState<InstructionPreset>('none')
  const [transformOptions, setTransformOptions] = useState<TransformOptions>({
    expandInstances: true,
    applyInstanceOverrides: true,
    applyComponentProps: true,
    filterHidden: true,
    detectStructuralFrames: true,
    detectIcons: true,
    sortChildrenByPosition: true,
    includePositionCoords: false,
    flattenStructuralWrappers: true,
    useFlexibleDimensions: true,
    includeNodeStyle: true,
    includeTextStyle: true,
    includeLayoutDimensions: true,
    includeMetadata: false,
  })

  const { parse, result, error, isLoading, clear } = useFigmaTestParser()

  const handleParse = () => {
    parse(clipboardInput, transformOptions)
  }

  const handleClear = () => {
    setClipboardInput('')
    clear()
  }

  const instructions = instructionPreset === 'tailwind' ? TAILWIND_INSTRUCTIONS : null
  const generatedPrompt = result
    ? componentTreeToPrompt(
        result.componentTree,
        result.fileName,
        result.nodeName,
        false,
        instructions,
      )
    : null

  return (
    <div className="flex h-screen flex-col">
      <div className="border-b bg-background px-6 py-4">
        <h1 className="text-2xl font-bold">Figma Clipboard Parser</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Test the Figma clipboard parser with configurable transformer options
        </p>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {!isLeftPanelCollapsed && (
          <div className="w-2/5 overflow-auto border-r bg-background p-6">
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Label>Instructions</Label>
                <Select
                  value={instructionPreset}
                  onValueChange={(v) => {
                    setInstructionPreset(v as InstructionPreset)
                  }}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="tailwind">Tailwind</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <ClipboardInput
                value={clipboardInput}
                onChange={setClipboardInput}
                onParse={handleParse}
                onClear={handleClear}
                isLoading={isLoading}
                error={error}
              />

              <div className="border-t pt-6">
                <TransformOptionsPanel options={transformOptions} onChange={setTransformOptions} />
              </div>
            </div>
          </div>
        )}

        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-0 top-4 z-10 h-8 w-8 rounded-r-md border border-l-0 bg-background shadow-sm hover:bg-muted"
            onClick={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}
          >
            {isLeftPanelCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        <div className="flex-1 overflow-auto bg-muted/30 p-6">
          {generatedPrompt && result ? (
            <ClipboardTestOutput
              prompt={generatedPrompt}
              componentTree={result.componentTree}
              rawClipboardJson={result.rawClipboardJson}
              page1Canvas={result.page1Canvas}
              internalCanvas={result.internalCanvas}
            />
          ) : (
            <ClipboardTestOutputEmpty />
          )}
        </div>
      </div>
    </div>
  )
}
